import React, { useMemo, useState } from "react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import Core from "../Components/Core";
import { motion } from "motion/react";

const OnlineEntrance = () => {
  const schoolId = "ebun";

  const initialFormData = {
    firstName: "",
    lastName: "",
    school: "",
    ad_no: "",
    parentEmail: "",
    parentPhone: "",
    gender: "",
    studentClass: "",
    department: "",
    state: "",
    website: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const classOptions = useMemo(
    () => [
      "Preparatory 1",
      "Preparatory 2",
      "Reception 1",
      "Reception 2",
      "PlayGroup",
      "Reception",
      "NUR 1",
      "NUR 2",
      "KG 1",
      "KG 2",
      "Grade 1",
      "Grade 2",
      "Grade 3",
      "Grade 4",
      "Grade 5",
      "Grade 6",
      "JSS 1",
      "JSS 2",
      "JSS 3",
      "SSS 1",
      "SSS 2",
      "SSS 3",
    ],
    []
  );

  const departmentOptions = useMemo(() => {
    return schoolId === "sac"
      ? ["NONE", "SCIENCE", "HUMANITIES", "BUSINESS"]
      : ["NONE", "SCIENCE", "ART", "COMMERCIAL"];
  }, [schoolId]);

  const schoolOptions = useMemo(() => ["LIASU", "AKESAN", "TOLU ALUKO"], []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      const juniorClasses = [
        "Preparatory 1",
        "Preparatory 2",
        "Reception 1",
        "Reception 2",
        "PlayGroup",
        "Reception",
        "NUR 1",
        "NUR 2",
        "KG 1",
        "KG 2",
        "Grade 1",
        "Grade 2",
        "Grade 3",
        "Grade 4",
        "Grade 5",
        "Grade 6",
      ];

      if (name === "studentClass" && juniorClasses.includes(value)) {
        updated.department = "NONE";
      }

      return updated;
    });
  };

  const validateEmails = (emailString) => {
    if (!emailString.trim()) return true;

    const emails = emailString
      .split(";")
      .map((e) => e.trim())
      .filter(Boolean);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.every((email) => emailRegex.test(email));
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) return true;
    return /^[0-9+\-\s()]{7,20}$/.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    if (formData.website) {
      setMessage({ type: "danger", text: "Invalid submission detected." });
      return;
    }

    if (!validateEmails(formData.parentEmail)) {
      setMessage({
        type: "danger",
        text: "Please enter valid parent email(s), separated by semicolons.",
      });
      return;
    }

    if (!validatePhone(formData.parentPhone)) {
      setMessage({
        type: "danger",
        text: "Please enter a valid parent phone number.",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("https://www.ebunheritageschool.com/upload/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          school_id: schoolId,
          student: {
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            school: formData.school.trim(),
            ad_no: formData.ad_no.trim(),
            parentEmail: formData.parentEmail.trim(),
            parentPhone: formData.parentPhone.trim(),
            gender: formData.gender.trim(),
            studentClass: formData.studentClass.trim(),
            department: formData.department.trim(),
            state: formData.state.trim(),
            website: formData.website.trim(),
          },
        }),
      });
    
      const text = await response.text();
    
      let result;
      try {
        result = text ? JSON.parse(text) : {};
      } catch (err) {
        console.error("Raw server response:", text);
        throw new Error("Server did not return valid JSON.");
      }
    
      if (!response.ok) {
        throw new Error(result.message || "Submission failed.");
      }
    
      setMessage({
        type: "success",
        text: result.message || "Student submitted successfully.",
      });
    
      setFormData(initialFormData);
    } catch (error) {
      setMessage({
        type: "danger",
        text: error.message || "Something went wrong while submitting the form.",
      });
    }
  };

  return (
    <div>
      <Navbar />

      <section className="bg-white pt-lg-8 pt-6 pb-lg-8 pb-6 px-lg-6 px-4">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-11">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="text-center mb-5">
                  <div className="fs-1 fw-medium text-dark mb-3">
                    Online Entrance Form
                  </div>
                  <p className="fs-4 text-muted mb-0">
                    Fill in the student details below and submit the form.
                  </p>
                </div>

                <div className="card border-0 shadow-none rounded-4 p-lg-5 p-4">
                  {message.text && (
                    <div className={`alert alert-${message.type} rounded-4 fs-5`}>
                      {message.text}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="row mb-4">
                      <div className="col-12 mb-5">
                        <div className="fw-medium fs-3 text-dark">
                          Student Information
                        </div>
                      </div>

                      <input
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        autoComplete="off"
                        tabIndex="-1"
                        style={{ display: "none" }}
                      />

                      <div className="col-lg-5 mb-4">
                        <label className="fs-4 text-dark mb-2">
                          Student First Name
                        </label>
                        <input
                          className="form-control form-control-md text-dark fs-4 rounded-pill"
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          placeholder="Enter student first name"
                          required
                        />
                      </div>

                      <div className="col-lg-5 mb-4">
                        <label className="fs-4 text-dark mb-2">
                          Student Last Name
                        </label>
                        <input
                          className="form-control form-control-md text-dark fs-4 rounded-pill"
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Enter student last name"
                          required
                        />
                      </div>

                      {schoolId === "ebun" && (
                        <div className="col-lg-2 mb-4">
                          <label className="fs-4 text-dark mb-2">School</label>
                          <select
                            className="form-control form-control-md text-dark fs-4 rounded-pill"
                            name="school"
                            value={formData.school}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select School</option>
                            {schoolOptions.map((school) => (
                              <option key={school} value={school}>
                                {school}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="col-lg-5 mb-4">
                        <label className="fs-4 text-dark mb-2">
                          Parent Email(s)
                        </label>
                        <input
                          className="form-control form-control-md text-dark fs-4 rounded-pill"
                          type="text"
                          name="parentEmail"
                          value={formData.parentEmail}
                          onChange={handleInputChange}
                          placeholder="Enter parent email(s), separated by ;"
                        />
                      </div>

                      <div className="col-lg-5 mb-4">
                        <label className="fs-4 text-dark mb-2">
                          Parent Phone No.
                        </label>
                        <input
                          className="form-control form-control-md text-dark fs-4 rounded-pill"
                          type="text"
                          name="parentPhone"
                          value={formData.parentPhone}
                          onChange={handleInputChange}
                          placeholder="Enter parent phone number"
                        />
                      </div>

                      <div className="col-lg-2 mb-4">
                        <label className="fs-4 text-dark mb-2">Gender</label>
                        <select
                          className="form-control form-control-md text-dark fs-4 rounded-pill"
                          name="gender"
                          value={formData.gender}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>

                      <div className="col-lg-4 mb-4">
                        <label className="fs-4 text-dark mb-2">
                          Student Class
                        </label>
                        <select
                          className="form-control form-control-md text-dark fs-4 rounded-pill"
                          name="studentClass"
                          value={formData.studentClass}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Class</option>
                          {classOptions.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-lg-4 mb-4">
                        <label className="fs-4 text-dark mb-2">
                          Student Department
                        </label>
                        <select
                          className="form-control form-control-md text-dark fs-4 rounded-pill"
                          name="department"
                          value={formData.department}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Department</option>
                          {departmentOptions.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-lg-4 mb-4">
                        <label className="fs-4 text-dark mb-2">
                          Student State
                        </label>
                        <input
                          className="form-control form-control-md text-dark fs-4 rounded-pill"
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          placeholder="State of origin"
                        />
                      </div>

                      <div className="col-12 mt-3 text-center">
                        <button
                          type="submit"
                          className="btn btn-dark rounded-pill px-5 py-3 fs-5"
                          disabled={loading}
                        >
                          {loading ? "Submitting..." : "Submit Form"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Core />
      <Footer />
    </div>
  );
};

export default OnlineEntrance;